import { challengeSpots, type ChallengeSpot, type GoalsDone } from "./challenge";
import { placeCollectables } from "./collectables";
import { hubEntries, hubLocked, hubNeeds, tierDone, unlockedBy, type HubEntry, type HubSpot } from "./hubs";
import { LadderState, mapLadder, type MapChallenge, type Tier } from "./ladder";
import type { Level } from "./level";
import type { MarkerSpot } from "./render/markers";
import { Run, type Goal, type MapGoals } from "./run";

// A map's challenges (#88), sim-free and three.js-free: the start spots, the
// hubs (#86), the ladder (#66) against the session's done goals, and every
// question about them (what's locked and why, what a marker's check shows,
// a goal's tier, what a newly done goal unlocks, a hub's list). Maps with hubs
// lock per tier (`hubLocked`); maps without lock per goal (`requires`), and
// `locked` / `needs` hide which. Game holds one per map and keeps the meshes,
// the camera and the respawns.

/** What a board needs of a map (a `MapDef`). */
export interface BoardMap {
  id: string;
  goals: MapGoals;
  challenges?: readonly MapChallenge[];
  hubs: readonly HubSpot[];
}

export class ChallengeBoard {
  /** The map's challenge hubs (#86); empty on a map without them. */
  readonly hubs: readonly HubSpot[];
  /** Tiers, locks and the stat points each goal pays. */
  readonly ladder: LadderState;
  /** Each goal's text, for "needs ..." lines. */
  private goalText: Map<string, string>;
  /** Tiers a goal just unlocked, for a callout once the step's other callouts are in. */
  private unlocked: Tier[] = [];

  private constructor(
    private mapId: string,
    /** Every challenge's start spot (where its marker goes on a map without hubs). */
    readonly spots: readonly ChallengeSpot[],
    hubs: readonly HubSpot[],
    /** The map's whole goal list (tasks too), in goal-list order. */
    private all: readonly Goal[],
    challenges: readonly MapChallenge[] | undefined,
    private goalsDone: GoalsDone,
  ) {
    this.hubs = hubs;
    this.goalText = new Map(all.map((g) => [g.id, g.text]));
    this.ladder = new LadderState(mapLadder(challenges, all.map((g) => g.id)), this.done);
  }

  /** A map's board, against the session's done goals. */
  static forMap(map: BoardMap, level: Level, goalsDone: GoalsDone): ChallengeBoard {
    const all = new Run(map.goals, placeCollectables(map.goals, level), { tasks: true }).goals;
    return new ChallengeBoard(map.id, challengeSpots(map.goals, level), map.hubs, all, map.challenges, goalsDone);
  }

  /** A board with nothing on it, before the first map loads. */
  static empty(goalsDone: GoalsDone): ChallengeBoard {
    return new ChallengeBoard("", [], [], [], [], goalsDone);
  }

  get hasHubs(): boolean {
    return this.hubs.length > 0;
  }

  /** The markers free skate shows: one per hub, or one per challenge on a map without hubs. */
  get markerSpots(): readonly MarkerSpot[] {
    return this.hasHubs ? this.hubs.map((h) => ({ id: h.id, kind: "hub" as const, x: h.x, z: h.z, label: String(h.tier) })) : this.spots;
  }

  /** The challenge markers free skate offers: none on a map with hubs, where the hubs' lists start them. */
  get freeSpots(): readonly ChallengeSpot[] {
    return this.hasHubs ? [] : this.spots;
  }

  /** Whether a goal on this map is done this session (the markers' checks). */
  done = (id: string): boolean => this.goalsDone.has(this.mapId, id);

  /**
   * Marks a goal done. The first time, returns the stat points its rung pays
   * (#66) and notes any tier it unlocks (#86); null if it was already done.
   */
  complete(goalId: string): number | null {
    if (!this.goalsDone.add(this.mapId, goalId)) return null;
    if (this.hasHubs) this.unlocked.push(...unlockedBy(this.ladder.rungs, goalId, this.done));
    return this.ladder.reward(goalId);
  }

  /** The latest tier unlocked since the last call ("Tier 2 unlocked" callout), and forgets the rest. */
  takeUnlocked(): Tier | undefined {
    const tier = this.unlocked.at(-1);
    this.unlocked = [];
    return tier;
  }

  /** A goal's tier on the ladder, or a hub's ("hub-2"). */
  tierOf(id: string): Tier | undefined {
    return this.hubs.find((h) => h.id === id)?.tier ?? this.ladder.rung(id)?.tier;
  }

  /**
   * What a locked goal (or hub, "hub-2") waits on, like "High Score: 15,000
   * and Collect S-K-A-T-E"; null when it's open. With hubs (#86) the locks
   * are per tier: "Finish the Warm up challenges (2 to go)".
   */
  needs = (id: string): string | null => {
    if (this.hasHubs) {
      const tier = this.tierOf(id);
      return tier ? hubNeeds(this.ladder.rungs, tier, this.done) : null;
    }
    const missing = this.ladder.missing(id);
    return missing.length ? missing.map((m) => this.goalText.get(m) ?? m).join(" and ") : null;
  };

  /** Whether a goal (or a hub, "hub-2") is still locked (grey markers, #66), per tier with hubs (#86). */
  locked = (id: string): boolean => {
    if (!this.hasHubs) return this.ladder.locked(id);
    const tier = this.tierOf(id);
    return tier !== undefined && hubLocked(this.ladder.rungs, tier, this.done);
  };

  /** A marker's check: its goal done, or for a hub every challenge on its tier. */
  markerDone = (id: string): boolean => {
    const hub = this.hubs.find((h) => h.id === id);
    return hub ? tierDone(this.ladder.rungs, hub.tier, this.done) : this.done(id);
  };

  /** A challenge's start spot. */
  spot(id: string): ChallengeSpot | undefined {
    return this.spots.find((s) => s.id === id);
  }

  /** A tier's hub list rows. */
  hubEntries(tier: Tier): HubEntry[] {
    return hubEntries(this.ladder.rungs, tier, this.spots, this.done);
  }

  /** The tiers whose hubs are open, in order (Pause > Challenges' "Go to hub" rows, #87); none on a map without hubs. */
  openTiers(): Tier[] {
    return this.hubs.filter((h) => !this.locked(h.id)).map((h) => h.tier);
  }

  /** A tier's hub, if it's open ("Go to hub", #87). */
  openHub(tier: Tier): HubSpot | undefined {
    const hub = this.hubs.find((h) => h.tier === tier);
    return hub && !this.locked(hub.id) ? hub : undefined;
  }

  /** The hub a challenge's "Back to hub" goes to: its tier's, else the first. */
  hubFor(challengeId: string): HubSpot | undefined {
    const tier = this.tierOf(challengeId);
    return this.hubs.find((h) => h.tier === tier) ?? this.hubs[0];
  }

  /** Free skate's Challenges list (#66): every goal, by tier, checked if done this session, with its lock and what it pays. */
  goals(): Goal[] {
    return this.all
      .map((g): Goal => {
        const rung = this.ladder.rung(g.id);
        return {
          ...g,
          done: this.done(g.id),
          progress: undefined,
          ...(rung ? { tier: rung.tier, statPoints: rung.statPoints, needs: this.needs(g.id) } : {}),
        };
      })
      .sort((a, b) => (a.tier ?? 1) - (b.tier ?? 1));
  }
}
